import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Persona, Theme } from '@/shared/types';
import { STEPS } from '@/shared/walkthrough-steps';

// =========================================================
// Theme provider
// =========================================================

type ThemeContextValue = {
  theme: Theme;
  themePreference: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function resolveSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// fork(oem): theme choice survives a reload. Storage helpers mirror the
// BrandContext pattern (versions/v1/shared/BrandContext.tsx) — an SSR guard so
// the snapshot renderer never touches `window`, and a try/catch so a private
// window or blocked site data degrades to the default instead of throwing.
const THEME_STORAGE_KEY = 'mock-ui-theme';

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredTheme(theme: Theme): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Private window / blocked storage — the session still works, it just
    // won't remember the choice.
  }
}

/**
 * fork(oem): initial preference. Reading storage in the initializer rather than
 * in an effect means the first paint is already the remembered theme, so a
 * dark-mode reload never flashes light. Without `window` — the snapshot
 * renderer, any future SSR pass — this is `light`, the documented default.
 */
function initialThemePreference(): Theme {
  return readStoredTheme() ?? 'light';
}

function ThemeProvider({ children }: { children: ReactNode }) {
  const [themePreference, setThemePreference] = useState<Theme>(initialThemePreference);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
    // fork(oem): resolve once up front for the same no-flash reason.
    themePreference === 'system' ? resolveSystemTheme() : themePreference,
  );

  // fork(oem): remember the choice. An effect rather than a write inside the
  // state updater, so React StrictMode's double invocation cannot double-write.
  useEffect(() => {
    writeStoredTheme(themePreference);
  }, [themePreference]);

  useEffect(() => {
    const apply = () => {
      const next =
        themePreference === 'system' ? resolveSystemTheme() : themePreference;
      setResolvedTheme(next);
      document.documentElement.dataset.theme = next;
    };
    apply();

    if (themePreference !== 'system') return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [themePreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: resolvedTheme,
      themePreference,
      setTheme: setThemePreference,
      toggleTheme: () =>
        setThemePreference((t) => (t === 'light' ? 'dark' : 'light')),
    }),
    [resolvedTheme, themePreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

// =========================================================
// Persona provider
// =========================================================

type PersonaContextValue = {
  persona: Persona;
  setPersona: (p: Persona) => void;
};

const PersonaContext = createContext<PersonaContextValue | null>(null);

function PersonaProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState<Persona>('Admin');
  const value = useMemo(() => ({ persona, setPersona }), [persona]);
  return <PersonaContext.Provider value={value}>{children}</PersonaContext.Provider>;
}

export function usePersona() {
  const ctx = useContext(PersonaContext);
  if (!ctx) throw new Error('usePersona must be used inside <PersonaProvider>');
  return ctx;
}

// =========================================================
// Walkthrough provider — current step, free-explore, dismiss
// =========================================================

type WalkthroughContextValue = {
  currentStep: number;
  total: number;
  freeExplore: boolean;
  dismissed: boolean;
  setCurrentStep: (n: number) => void;
  next: () => void;
  prev: () => void;
  setFreeExplore: (f: boolean) => void;
  toggleFreeExplore: () => void;
  dismiss: () => void;
  resume: () => void;
};

const WalkthroughContext = createContext<WalkthroughContextValue | null>(null);

function WalkthroughProvider({ children }: { children: ReactNode }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [freeExplore, setFreeExplore] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      currentStep,
      total: STEPS.length,
      freeExplore,
      dismissed,
      setCurrentStep,
      next: () => setCurrentStep((n) => Math.min(n + 1, STEPS.length - 1)),
      prev: () => setCurrentStep((n) => Math.max(n - 1, 0)),
      setFreeExplore,
      toggleFreeExplore: () => setFreeExplore((f) => !f),
      dismiss: () => setDismissed(true),
      resume: () => setDismissed(false),
    }),
    [currentStep, freeExplore, dismissed],
  );

  return <WalkthroughContext.Provider value={value}>{children}</WalkthroughContext.Provider>;
}

export function useWalkthrough() {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) throw new Error('useWalkthrough must be used inside <WalkthroughProvider>');
  return ctx;
}

// =========================================================
// Drawer provider — universal right-anchored drawer with stack
// support (max depth 3). Scenes call `open(kind, props)` to push a
// drawer, `close()` to pop the topmost, and `closeAll()` to clear.
// The Drawer chrome component reads from this context to render.
// =========================================================

// Drawer kinds. The foundation ships four:
//   - 'entity-form'      — schema-driven create/edit form (EntityFormDrawer)
//   - 'citation'         — source excerpt opened from a CitationChip
//   - 'meeting-citation' — verbatim quote + action state (changelog timeline)
//   - 'placeholder'      — generic stub body for kinds not yet wired
// When a module needs its own drawer, add a kind here and register the body
// component in chrome/Drawer.tsx's renderBody switch.
export type DrawerKind =
  | 'entity-form'
  | 'citation'
  | 'meeting-citation'
  | 'placeholder'
  // fork(oem): source provenance (table slice / lake doc) — chrome-oem/SourceDrawerBody
  | 'oem-source'
  // fork(oem): entity peek (machine / WO / part / case) — chrome-oem/RecordDrawerBody
  | 'oem-record';

// The props passed to a drawer kind are intentionally open-ended for v1 —
// scenes can pass arbitrary metadata (e.g. citation id, conflict id, action
// id). The Drawer chrome only reads `wide` and forwards the rest to the
// per-kind body component when those are wired up.
export type DrawerProps = {
  wide?: boolean;
  title?: string;
  kicker?: string;
  // Allow free-form data for kind-specific bodies. Typed loosely as a
  // record of unknown so we don't pull in `any`.
  data?: Record<string, unknown>;
};

export type DrawerStackEntry = {
  id: number;
  kind: DrawerKind;
  props: DrawerProps;
};

type DrawerContextValue = {
  stack: DrawerStackEntry[];
  open: (kind: DrawerKind, props?: DrawerProps) => void;
  close: () => void;
  closeAll: () => void;
};

const DrawerContext = createContext<DrawerContextValue | null>(null);

const MAX_DRAWER_DEPTH = 3;

function DrawerProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<DrawerStackEntry[]>([]);
  const nextIdRef = useRef<number>(1);

  const open = useCallback((kind: DrawerKind, props: DrawerProps = {}) => {
    setStack((prev) => {
      // Cap the stack at MAX_DRAWER_DEPTH; if at limit, replace the top.
      const id = nextIdRef.current++;
      const entry: DrawerStackEntry = { id, kind, props };
      if (prev.length >= MAX_DRAWER_DEPTH) {
        return [...prev.slice(0, prev.length - 1), entry];
      }
      return [...prev, entry];
    });
  }, []);

  const close = useCallback(() => {
    setStack((prev) => (prev.length === 0 ? prev : prev.slice(0, prev.length - 1)));
  }, []);

  const closeAll = useCallback(() => {
    setStack([]);
  }, []);

  // Global ESC dismisses topmost drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Don't preempt other ESC handlers if no drawer is open.
        setStack((prev) => (prev.length === 0 ? prev : prev.slice(0, prev.length - 1)));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo<DrawerContextValue>(
    () => ({ stack, open, close, closeAll }),
    [stack, open, close, closeAll],
  );

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
}

export function useDrawer() {
  const ctx = useContext(DrawerContext);
  if (!ctx) throw new Error('useDrawer must be used inside <DrawerProvider>');
  return ctx;
}

// =========================================================
// Composed Providers wrapper
// =========================================================

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider>
      <PersonaProvider>
        <WalkthroughProvider>
          <DrawerProvider>{children}</DrawerProvider>
        </WalkthroughProvider>
      </PersonaProvider>
    </ThemeProvider>
  );
}
